/** Public status and options types of the sync engine. */

export const SyncState = {
  /** Sync is not configured, or the engine is stopped. */
  Disabled: 'disabled',
  Idle: 'idle',
  Syncing: 'syncing',
  /** The last attempt could not reach the server; retries run with exponential backoff. */
  Offline: 'offline',
  /** The server rejected the last attempt; see `lastError`. */
  Error: 'error',
  /**
   * The server has no space for this device's passphrase (404): its cloud data was deleted. The
   * engine neither recreates the space nor retries; the user chooses to upload this device's
   * data again or to disconnect this device.
   */
  SpaceMissing: 'spaceMissing',
} as const;
export type SyncState = (typeof SyncState)[keyof typeof SyncState];

export const SyncErrorCode = {
  /** No connection, or the request timed out. */
  Network: 'network',
  /** The write token does not match the space (403), or the token is malformed (401). Auto-sync stops. */
  Forbidden: 'forbidden',
  Unauthorized: 'unauthorized',
  /**
   * Creating the space again was refused: the server only offers sync to accounts
   * (`WIZTIME_PASSPHRASE_SPACES`) and this device's token does not belong to an account. The state stays
   * `SyncState.SpaceMissing`; auto-sync stays paused.
   */
  PassphraseSpacesDisabled: 'passphrase_spaces_disabled',
  /** The space does not exist on the server any more; see `SyncState.SpaceMissing`. */
  SpaceMissing: 'space_missing',
  /** The space is full (50 MB or 200,000 records); pulling continues, pushing is refused. */
  QuotaExceeded: 'quota_exceeded',
  PayloadTooLarge: 'payload_too_large',
  RateLimited: 'rate_limited',
  ServiceUnavailable: 'service_unavailable',
  InvalidRequest: 'invalid_request',
  ServerError: 'server_error',
  /**
   * The server requires a verified email before accepting data (`WIZTIME_EMAIL_VERIFICATION=required`) and this
   * account has none. Local edits stay in the local database and are pushed after verification; pulling and the
   * automatic retries continue.
   */
  EmailUnverified: 'email_unverified',
  /** The server's answer did not follow the protocol. */
  Protocol: 'protocol',
  /** Reading or writing the local database failed. */
  Storage: 'storage',
  /** Sync is configured but the encryption keys are missing on this device. */
  KeysMissing: 'keys_missing',
  Unexpected: 'unexpected',
} as const;
export type SyncErrorCode = (typeof SyncErrorCode)[keyof typeof SyncErrorCode];

export interface SyncStatus {
  readonly state: SyncState;
  /** Epoch milliseconds of the last successful sync; null before the first one. */
  readonly lastSyncAt: number | null;
  /** Reason of the last failure; null while the last attempt succeeded. */
  readonly lastError: SyncErrorCode | null;
  /** Records skipped since the engine started because they could not be decrypted or validated. */
  readonly skippedRecords: number;
  /** Epoch milliseconds of the next automatic retry after a failure; null when none is scheduled. */
  readonly retryAt: number | null;
}

export type SyncStatusListener = (status: SyncStatus) => void;

export interface SyncCounts {
  /** Records the server accepted from this device. */
  readonly pushed: number;
  /** Records received from the server. */
  readonly pulled: number;
  /** Received records that changed the local store (newer than the local version). */
  readonly applied: number;
  /** Received or outgoing records that were skipped. */
  readonly skipped: number;
}

export type SyncRunOutcome =
  | ({ readonly ok: true } & SyncCounts)
  /** `error` is null when the run was cancelled because the engine stopped. */
  | { readonly ok: false; readonly error: SyncErrorCode | null; readonly state: SyncState };

export interface SyncLogger {
  debug(message: string, context?: Record<string, unknown>): void;
  info(message: string, context?: Record<string, unknown>): void;
  warn(message: string, context?: Record<string, unknown>): void;
  error(message: string, context?: Record<string, unknown>): void;
}

export const silentLogger: SyncLogger = {
  debug: () => undefined,
  info: () => undefined,
  warn: () => undefined,
  error: () => undefined,
};
